
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DialogClose } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

interface FormData {
  name: string;
  email: string;
  company: string;
  interest?: string;
  message: string;
}

interface ContactFormProps {
  type: 'discovery' | 'demo' | 'contact';
  buttonText: string;
  onFormSuccess: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({
  type,
  buttonText,
  onFormSuccess
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    interest: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSelectChange = (value: string) => {
    setFormData(prev => ({ ...prev, interest: value }));
  };

  const validateForm = () => {
    if (!formData.name) return "Name is required";
    if (!formData.email) return "Email is required";
    if (!formData.email.includes('@')) return "Please enter a valid email address";
    if (!formData.message) return "Message is required";
    return null;
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const error = validateForm();
    if (error) {
      setFormError(error);
      return;
    }
    
    setIsSubmitting(true);
    setFormError(null);
    
    try {
      const form = e.currentTarget;
      const formData = new FormData(form);
      
      const response = await fetch(form.action, {
        method: form.method,
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      
      if (response.ok) {
        toast({
          title: "Request Submitted",
          description: "Thank you for your interest! We'll be in touch with you shortly.",
        });
        form.reset();
        onFormSuccess();
      } else {
        const data = await response.json();
        if (data.errors) {
          setFormError(data.errors.map((error: any) => error.message).join(", "));
        } else {
          setFormError("There was a problem submitting your request. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error sending form:", error);
      setFormError("There was a problem submitting your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form 
      onSubmit={handleFormSubmit} 
      action="https://formspree.io/f/xwplpoyz" 
      method="POST" 
      className="space-y-4 pt-4"
    >
      {formError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      )}
      
      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Full Name</Label>
          <Input 
            id="name"
            name="Full Name"
            placeholder="John Doe" 
            value={formData.name} 
            onChange={handleChange} 
            required 
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="email">Email Address</Label>
          <Input 
            id="email" 
            name="Email Address"
            type="email" 
            placeholder="john@example.com" 
            value={formData.email} 
            onChange={handleChange}
            required 
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="company">Company Name</Label>
          <Input 
            id="company"
            name="Company Name"
            placeholder="Acme Inc." 
            value={formData.company} 
            onChange={handleChange}
          />
        </div>
        
        {type === 'discovery' && (
          <div className="space-y-2">
            <Label htmlFor="interest">What are you interested in?</Label>
            <input type="hidden" name="Interest" value={formData.interest || ''} />
            <Select onValueChange={handleSelectChange} value={formData.interest}>
              <SelectTrigger>
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="consulting">AI Consulting & Strategy</SelectItem>
                <SelectItem value="automation">AI Automation</SelectItem>
                <SelectItem value="research">AI Research & Persona Development</SelectItem>
                <SelectItem value="voice">Voice AI & Assistants</SelectItem>
                <SelectItem value="content">AI Content Development</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        
        <div className="space-y-2">
          <Label htmlFor="message">Message</Label>
          <Textarea 
            id="message"
            name="Message"
            placeholder="Tell us about your needs or questions..." 
            className="min-h-[100px]" 
            value={formData.message} 
            onChange={handleChange}
            required
          />
        </div>
        
        {/* Hidden fields for additional information */}
        <input type="hidden" name="Request Type" value={type} />
        <input type="hidden" name="Email to" value="scott@sparkwave-ai.com" />
        
        {/* Formspree configuration for email to recipient (you) */}
        <input type="hidden" name="_subject" value={`Sparkwave AI: New ${type.charAt(0).toUpperCase() + type.slice(1)} Request`} />
        
        {/* Formspree configuration for autoresponse to sender */}
        <input type="hidden" name="_autoresponse" value="Thank you for contacting Sparkwave AI. We've received your message and will get back to you shortly." />
      </div>
      
      <div className="flex justify-end gap-3 pt-2">
        <DialogClose asChild>
          <Button type="button" variant="outline">Cancel</Button>
        </DialogClose>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : buttonText}
        </Button>
      </div>
    </form>
  );
};

export default ContactForm;
